import { AxiosResponse } from 'axios'
import { getterTree, mutationTree, actionTree } from 'typed-vuex'
import { map, sum } from 'lodash-es'
import { setState } from '~/lib/utils/store'
import { ALL_TAB, PICKED_TAB, STARTED_TAB, IGNORED_TAB, RECOMMENDED_TAB, SENT_TAB, ACTIVE_SUB_TAB, CLOSED_SUB_TAB, EASY_APPLY_TAB } from '~/store/sideBar'
import { sortDefaultMatches, sortByDate, SORT_STATUS_UPDATE, sortByStatusUpdated } from '~/lib/utils/sorting'
import { filterBy, filterActiveStatuses, filterClosedStatus, STATUS_ID_CANCELED, suitableCardsOnTop } from '~/lib/utils/filters'
import { SCHOLARSHIP_LIST } from '~/store/cardList'
import { Store } from '~/types/store'

export function isCapBased(item: Scholarship.General, isFlagEnabled = false): boolean {
  return isFlagEnabled && !!item.applicationCap && item.applicationCap > 0
}

export function isCapBasedFull(item: Scholarship.General, isFlagEnabled = false): boolean {
  if (!isFlagEnabled) { return false }
  if (item.capUrgency === 'full') { return true }
  return isCapBased(item, isFlagEnabled) && item.applicationCount >= item.applicationCap!
}

export const state = () => ({
  initEasyScholarshipsLength: undefined as undefined | number,
  recentlyIgnoredScholarshipId: null as Scholarship.General['scholarshipId'] | null,
  isStatusPopoverSeen: false
})

export const getters = getterTree(
  state,
  {
    [ALL_TAB](state, getter, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      return filterBy(list, item => !item.isIgnored && item.status !== STATUS_ID_CANCELED)
    },
    [PICKED_TAB](state, getters, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      return filterBy(list, item => !!item.isFavorite)
    },
    [STARTED_TAB](state, getters, rootState, rootGetters) {
      return filterBy(
        rootGetters['cardList/scholarships'],
        item => (item.requirementApplicationCount > 0)).sort((a, b) => (b.lastUpdatedAt - a.lastUpdatedAt)
      )
    },
    [IGNORED_TAB](state, getters, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      return filterBy(list, item => !!item.isIgnored)
    },
    [RECOMMENDED_TAB](state, getters, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      const isRecommended = rootGetters['recommendation/isRecommended']
      return filterBy(
        list,
        item => isRecommended(item) && !item.isIgnored
      )
    },
    [SENT_TAB](state, getters, rootState, rootGetters) {
      const sortingType = rootState.sorting[SENT_TAB].sort
      if (sortingType.sortField === SORT_STATUS_UPDATE) {
        return sortByStatusUpdated(rootGetters['cardList/applications'], sortingType.sortDir)
      } else {
        return sortByDate(rootGetters['cardList/applications'])
      }
    },
    [ACTIVE_SUB_TAB](state, getters, rootState, rootGetters) {
      return sortByDate(filterActiveStatuses(rootGetters['cardList/applications']))
    },
    [CLOSED_SUB_TAB](state, getters, rootState, rootGetters) {
      return sortByDate(filterClosedStatus(rootGetters['cardList/applications']))
    },
    [EASY_APPLY_TAB](state, getters, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      return filterBy(list, item => !item.isIgnored && !!item.isEasy && item.status !== STATUS_ID_CANCELED)
    },
    totalCount(state, getters) {
      return (tabName: Store.TabName) => {
        if (!getters[tabName]) { return 0 }
        return getters[tabName].length
      }
    },
    notSeenAll(state, getters, rootState, rootGetters) {
      const list = rootGetters['cardList/scholarships']
      if (!list.length) { return 0 }

      return list.reduce((acc: number, card:Scholarship.General) =>
        (acc + (card?.isSeen || card?.isIgnored || card.status === STATUS_ID_CANCELED ? 0 : 1)), 0)
    },
    notSeenSent(state, getters) {
      if (!getters[SENT_TAB]) { return 0 }
      return getters[SENT_TAB].reduce(
        (acc: number, card:Scholarship.GeneralSent) => (acc - (isNaN(card.externalStatusUpdateSeen) ? 1 : card.externalStatusUpdateSeen)),
        getters[SENT_TAB].length
      )
    },
    tabList(state, getters, rootState) {
      const pointer = rootState.route.params.subTabName
        ? rootState.route.params.subTabName
        : rootState.route.params.tabName

      if (!pointer) { return [] }
      return getters[pointer]
    },
    processedList(state, getters, rootState, rootGetters) {
      const pointer = rootState.route.params.subTabName
        ? rootState.route.params.subTabName
        : rootState.route.params.tabName

      if (!getters[pointer]) { return getters.tabList }

      const list = rootGetters['sorting/sortedList'](getters.tabList)
      return rootGetters['filters/filteredList'](pointer, list)
    },
    worthOfAllMatches(_state, getters) {
      return sum(map(getters[ALL_TAB], 'amount'))
    }
  }
)

export const mutations = mutationTree(
  state,
  {
    setState
  }
)

export const actions = actionTree(
  { state, getters },
  {
    // scholarship state
    saveCard(store, scholarshipId: string): Promise<AxiosResponse<any, any>> {
      return this.$api.scholarship.markFavorite({ id: scholarshipId }, {})
        .then((res: Axios.AxiosResponse) => {
          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isFavorite',
            propValue: 1
          })

          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isIgnored',
            propValue: 0
          })

          return res
        })
    },

    unsaveCard(store, scholarshipId: string): Promise<AxiosResponse<any, any>> {
      return this.$api.scholarship.unmarkFavorite({ id: scholarshipId }, {})
        .then((res: Axios.AxiosResponse) => {
          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isFavorite',
            propValue: 0
          })

          return res
        })
    },

    ignoreCard({ commit }, scholarshipId: number): Promise<AxiosResponse<any, any>> {
      return this.$api.scholarship.markIgnored({ id: scholarshipId }, {})
        .then((res: Axios.AxiosResponse) => {
          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isIgnored',
            propValue: 1
          })

          commit('setState', { stateName: 'recentlyIgnoredScholarshipId', data: scholarshipId })

          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isFavorite',
            propValue: 0
          })

          return res
        })
    },

    unignoreCard(store, scholarshipId: string): Promise<AxiosResponse<any, any>> {
      return this.$api.scholarship.markUnignored({ id: scholarshipId }, {})
        .then((res: Axios.AxiosResponse) => {
          this.app.$accessor.cardList.changeItemProperty({
            storeName: SCHOLARSHIP_LIST,
            id: scholarshipId,
            propName: 'isIgnored',
            propValue: 0
          })

          return res
        })
    }
  })
